<template>
  <div class="behavior-study" v-loading="loading">
    <header class="study-toolbar">
      <div>
        <h2>User Behavior Study</h2>
        <p>Distinct accounts with a login session active in the selected period.</p>
      </div>
      <div class="toolbar-actions">
        <el-radio-group v-model="rangeDays" size="small" @change="fetchStudy">
          <el-radio-button :label="7">7D</el-radio-button>
          <el-radio-button :label="30">30D</el-radio-button>
          <el-radio-button :label="90">90D</el-radio-button>
        </el-radio-group>
        <el-button size="small" icon="el-icon-refresh" @click="fetchStudy">Refresh</el-button>
      </div>
    </header>

    <el-alert v-if="errorMessage" :title="errorMessage" type="error" show-icon :closable="false" class="study-error" />
    <el-empty v-if="!loading && !study" description="No behavior data available" />

    <template v-if="study">
      <section class="metric-strip" aria-label="Account summary">
        <div v-for="metric in summaryMetrics" :key="metric.label" class="metric-tile">
          <span>{{ metric.label }}</span>
          <strong>{{ formatNumber(metric.value) }}</strong>
          <small>{{ metric.note }}</small>
        </div>
      </section>

      <div class="study-grid">
        <section class="study-panel">
          <div class="panel-heading">
            <h3>Platform mix</h3>
            <p>Each account appears in exactly one group.</p>
          </div>
          <div v-for="row in platformRows" :key="row.key" class="distribution-row">
            <div class="row-top"><span>{{ row.label }}</span><strong>{{ formatNumber(row.value) }} <small>{{ percent(row.value) }}</small></strong></div>
            <div class="bar-track"><span :style="barStyle(row.value, row.color)"></span></div>
          </div>
        </section>

        <section class="study-panel">
          <div class="panel-heading">
            <h3>Saved reading layout</h3>
            <p>Current account preference for Card or Flow view.</p>
          </div>
          <div v-for="row in layoutRows" :key="row.key" class="distribution-row">
            <div class="row-top"><span>{{ row.label }}</span><strong>{{ formatNumber(row.value) }} <small>{{ percent(row.value) }}</small></strong></div>
            <div class="bar-track"><span :style="barStyle(row.value, row.color)"></span></div>
          </div>
          <p class="panel-footnote">No saved choice includes accounts still using the default Card view.</p>
        </section>
      </div>

      <div class="study-grid lower-grid">
        <section class="study-panel">
          <div class="panel-heading">
            <h3>Platform reach</h3>
            <p>Distinct accounts per platform. One account may appear in several rows.</p>
          </div>
          <div v-for="row in reachRows" :key="row.key" class="distribution-row compact">
            <div class="row-top"><span>{{ row.label }}</span><strong>{{ formatNumber(row.value) }} <small>{{ percent(row.value) }}</small></strong></div>
            <div class="bar-track"><span :style="barStyle(row.value, row.color)"></span></div>
          </div>
        </section>

        <section class="study-panel matrix-panel">
          <div class="panel-heading">
            <h3>Layout by platform mix</h3>
            <p>Current preference among accounts in each platform group.</p>
          </div>
          <div class="matrix-scroll">
            <table>
              <thead><tr><th scope="col">Platform use</th><th scope="col">Card</th><th scope="col">Flow</th><th scope="col">No choice</th><th scope="col">Total</th></tr></thead>
              <tbody>
                <tr v-for="row in matrixRows" :key="row.key">
                  <th scope="row">{{ row.label }}</th>
                  <td>{{ formatNumber(row.card) }}</td>
                  <td>{{ formatNumber(row.paragraph) }}</td>
                  <td>{{ formatNumber(row.unset) }}</td>
                  <td class="row-total">{{ formatNumber(row.total) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>

      <p class="method-note">
        Platform use is based on login sessions active in the trailing {{ rangeDays }} days. Layout is the latest saved account choice, not a history of reading events. SKIM mode is not measured here.
      </p>
    </template>
  </div>
</template>

<script>
import { getUserBehaviorStudy } from '@/apis/lfAdmin'

const PLATFORM_GROUPS = [
  { key: 'web_only', label: 'Web only', color: '#2563eb' },
  { key: 'app_only', label: 'App only', color: '#0f9f83' },
  { key: 'both', label: 'Web + app', color: '#8b5cf6' }
]
const LAYOUT_GROUPS = [
  { key: 'card', label: 'Card view', color: '#2563eb' },
  { key: 'paragraph', label: 'Flow view', color: '#0f9f83' },
  { key: 'unset', label: 'No saved choice', color: '#a4adbb' }
]
const REACH_GROUPS = [
  { key: 'web_desktop', label: 'Desktop web', color: '#2563eb' },
  { key: 'web_mobile', label: 'Mobile web', color: '#60a5fa' },
  { key: 'app_ios', label: 'iOS app', color: '#0f9f83' },
  { key: 'app_android', label: 'Android app', color: '#46bba7' },
  { key: 'app_other', label: 'Other app', color: '#95b8ae' }
]

export default {
  name: 'LFAdminUserBehavior',
  data() {
    return { rangeDays: 30, loading: false, study: null, errorMessage: '', requestId: 0 }
  },
  computed: {
    activeAccounts() { return Number((this.study && this.study.active_accounts) || 0) },
    platformMix() { return (this.study && this.study.platform_mix) || {} },
    layoutMix() { return (this.study && this.study.layout_mix) || {} },
    summaryMetrics() {
      return [
        { label: 'Active accounts', value: this.activeAccounts, note: `Trailing ${this.rangeDays} days` },
        { label: 'Used web', value: Number(this.platformMix.web_only || 0) + Number(this.platformMix.both || 0), note: 'Includes web + app' },
        { label: 'Used app', value: Number(this.platformMix.app_only || 0) + Number(this.platformMix.both || 0), note: 'Includes web + app' },
        { label: 'Saved a layout', value: Number(this.layoutMix.card || 0) + Number(this.layoutMix.paragraph || 0), note: 'Card or Flow choice' }
      ]
    },
    platformRows() { return PLATFORM_GROUPS.map(item => ({ ...item, value: Number(this.platformMix[item.key] || 0) })) },
    layoutRows() { return LAYOUT_GROUPS.map(item => ({ ...item, value: Number(this.layoutMix[item.key] || 0) })) },
    reachRows() {
      const reach = (this.study && this.study.platform_reach) || {}
      return REACH_GROUPS.map(item => ({ ...item, value: Number(reach[item.key] || 0) }))
    },
    matrixRows() {
      const matrix = (this.study && this.study.layout_by_platform) || {}
      return PLATFORM_GROUPS.map(item => {
        const values = matrix[item.key] || {}
        return { ...item, card: values.card || 0, paragraph: values.paragraph || 0, unset: values.unset || 0, total: values.total || 0 }
      })
    }
  },
  created() { this.fetchStudy() },
  methods: {
    async fetchStudy() {
      const requestId = ++this.requestId
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await getUserBehaviorStudy({ days: this.rangeDays })
        if (requestId !== this.requestId) return
        if (!response || !response.data || Number(response.data.code) !== 0) {
          throw new Error((response && response.data && response.data.msg) || 'Could not load user behavior data')
        }
        this.study = response.data.data
      } catch (error) {
        if (requestId !== this.requestId) return
        this.errorMessage = error.message || 'Could not load user behavior data'
        this.study = null
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    formatNumber(value) { return Number(value || 0).toLocaleString() },
    percent(value) { return this.activeAccounts ? `${(Number(value || 0) / this.activeAccounts * 100).toFixed(1)}%` : '0.0%' },
    barStyle(value, color) { return { width: this.percent(value), backgroundColor: color } }
  }
}
</script>

<style scoped lang="scss">
.behavior-study { padding: 28px 30px 34px; color: #233047; max-width: 1500px; margin: 0 auto; }
.study-toolbar { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 22px; }
.study-toolbar h2 { font-size: 24px; line-height: 1.2; margin: 0 0 6px; }
.study-toolbar p, .panel-heading p { margin: 0; color: #718096; font-size: 13px; line-height: 1.5; }
.toolbar-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.study-error { margin-bottom: 18px; }
.metric-strip { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin-bottom: 16px; }
.metric-tile, .study-panel { background: #fff; border: 1px solid #e5eaf1; border-radius: 12px; box-shadow: 0 4px 18px rgba(31, 48, 74, .04); }
.metric-tile { min-height: 100px; padding: 17px 20px; display: flex; flex-direction: column; }
.metric-tile span { font-size: 12px; color: #718096; font-weight: 600; }
.metric-tile strong { font-size: 30px; line-height: 1.1; margin: 10px 0 6px; font-variant-numeric: tabular-nums; }
.metric-tile small { color: #8a95a5; font-size: 11px; }
.study-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; margin-bottom: 16px; }
.study-panel { min-width: 0; padding: 22px 24px 24px; }
.panel-heading { margin-bottom: 22px; }
.panel-heading h3 { margin: 0 0 5px; font-size: 16px; }
.distribution-row + .distribution-row { margin-top: 18px; }
.distribution-row.compact + .distribution-row.compact { margin-top: 14px; }
.row-top { display: flex; justify-content: space-between; gap: 10px; font-size: 13px; margin-bottom: 8px; }
.row-top strong { white-space: nowrap; font-variant-numeric: tabular-nums; }
.row-top small { font-size: 11px; color: #8995a7; font-weight: 500; margin-left: 7px; }
.bar-track { height: 9px; border-radius: 8px; background: #edf1f6; overflow: hidden; }
.bar-track span { display: block; height: 100%; border-radius: inherit; }
.panel-footnote, .method-note { color: #8090a3; font-size: 12px; line-height: 1.55; }
.panel-footnote { margin: 18px 0 0; }
.method-note { margin: 5px 1px 0; }
.matrix-scroll { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; min-width: 465px; font-size: 12px; }
th, td { padding: 11px 8px; border-bottom: 1px solid #edf1f6; text-align: right; font-variant-numeric: tabular-nums; }
thead th { color: #718096; font-weight: 600; white-space: nowrap; }
th:first-child { text-align: left; }
tbody th { color: #334155; font-weight: 500; }
tbody tr:last-child th, tbody tr:last-child td { border-bottom: 0; }
.row-total { font-weight: 700; }
@media (max-width: 1100px) { .metric-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); } .study-grid { grid-template-columns: 1fr; } }
@media (max-width: 650px) { .behavior-study { padding: 18px 14px 26px; } .study-toolbar { flex-direction: column; } .metric-strip { gap: 9px; } .metric-tile { padding: 14px; } .metric-tile strong { font-size: 24px; } .study-panel { padding: 18px; } }
</style>
